import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';

type ProposalState = 'generando' | 'pendiente' | 'aprobada' | 'rechazada' | 'error' | 'cancelada';
type FamilyExercise = { exerciseId: number; exerciseName: string; series: number; repetitions: number; restSeconds: number };
type FamilySession = { code: string; name: string; exercises: FamilyExercise[] };
type FamilyStage = { code: string; name: string; durationDays: number; minimumSessions: number | null; sessions: FamilySession[] };
type Family = { name: string; description: string; frequency: number; level: string; stages: FamilyStage[] };
type ProposalPayload = { title?: string; summary?: string; families?: Family[]; coachReviewNotes?: string[] };
type Proposal = {
  id: string;
  usuario_id: string | null;
  solicitada_por_admin_id: string | null;
  estado: ProposalState;
  instrucciones: string;
  propuesta: ProposalPayload | null;
  error_acoma: { message?: string; code?: string } | null;
  created_at: string;
  generacion_finalizada_at: string | null;
  comentario_revision: string | null;
};
type RoutineProposal = {
  id: string;
  usuario_id: string;
  estado: 'pendiente' | 'aprobada' | 'rechazada' | 'cancelada';
  resumen: string;
  motivo: Record<string, unknown> | null;
  created_at: string;
  rutina_propuesta: { id: string; nombre: string } | null;
};

const stateLabels: Record<ProposalState, string> = {
  generando: 'Generando', pendiente: 'Por revisar', aprobada: 'Aprobada', rechazada: 'Rechazada', error: 'Con error', cancelada: 'Cancelada',
};
const stateStyles: Record<ProposalState, string> = {
  generando: 'bg-blue-100 text-blue-700', pendiente: 'bg-amber-100 text-amber-800', aprobada: 'bg-emerald-100 text-emerald-700',
  rechazada: 'bg-red-100 text-red-700', error: 'bg-rose-100 text-rose-700', cancelada: 'bg-gray-100 text-gray-600',
};

export default function PropuestasIAPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { sucursalActiva } = useSucursal();
  const gymId = profile?.gimnasioId || sucursalActiva?.gimnasio_id || null;
  const [rows, setRows] = useState<Proposal[]>([]);
  const [routineRows, setRoutineRows] = useState<RoutineProposal[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [requesterNames, setRequesterNames] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<'activas' | 'todas'>('activas');
  const [selected, setSelected] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!gymId) return;
    setLoading(true);
    setError(null);
    const [familyResult, routineResult] = await Promise.all([
      supabase
        .from('rutina_familia_recomendaciones_ia')
        .select('id,usuario_id,solicitada_por_admin_id,estado,instrucciones,propuesta,error_acoma,created_at,generacion_finalizada_at,comentario_revision')
        .eq('gimnasio_id', gymId)
        .eq('tipo_propuesta', 'familia')
        .order('created_at', { ascending: false })
        .limit(200),
      supabase
        .from('rutina_recomendaciones_ia')
        .select('id,usuario_id,estado,resumen,motivo,created_at,rutina_propuesta:rutinas!rutina_recomendaciones_ia_rutina_propuesta_id_fkey(id,nombre)')
        .eq('gimnasio_id', gymId)
        .order('created_at', { ascending: false })
        .limit(200),
    ]);
    const loadError = familyResult.error || routineResult.error;
    if (loadError) {
      setError(loadError.message);
      setRows([]);
      setRoutineRows([]);
      setLoading(false);
      return;
    }
    const proposals = (familyResult.data || []) as Proposal[];
    const routineProposals = (routineResult.data || []) as unknown as RoutineProposal[];
    setRows(proposals);
    setRoutineRows(routineProposals);
    setSelected((current) => current ? proposals.find((row) => row.id === current.id) || null : null);

    const userIds = [...new Set([
      ...proposals.map((row) => row.usuario_id),
      ...routineProposals.map((row) => row.usuario_id),
    ].filter(Boolean))] as string[];
    const requesterIds = [...new Set(proposals.map((row) => row.solicitada_por_admin_id).filter(Boolean))] as string[];
    const [members, requesters] = await Promise.all([
      userIds.length ? supabase.from('usuarios').select('id,nombre,email').in('id', userIds) : Promise.resolve({ data: [], error: null }),
      requesterIds.length ? supabase.from('usuarios_admin').select('id,nombre').in('id', requesterIds) : Promise.resolve({ data: [], error: null }),
    ]);
    if (members.error) setError(members.error.message);
    if (requesters.error) setError(requesters.error.message);
    setNames(Object.fromEntries((members.data || []).map((row: any) => [row.id, `${row.nombre}${row.email ? ` · ${row.email}` : ''}`])));
    setRequesterNames(Object.fromEntries((requesters.data || []).map((row: any) => [row.id, row.nombre])));
    setLoading(false);
  }, [gymId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!gymId) return;
    const channel = supabase.channel(`family-proposals:${gymId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rutina_familia_recomendaciones_ia', filter: `gimnasio_id=eq.${gymId}` }, () => void load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rutina_recomendaciones_ia', filter: `gimnasio_id=eq.${gymId}` }, () => void load())
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [gymId, load]);

  const visible = useMemo(() => filter === 'todas' ? rows : rows.filter((row) => ['generando', 'pendiente', 'error'].includes(row.estado)), [filter, rows]);
  const visibleRoutineRows = useMemo(() => filter === 'todas' ? routineRows : routineRows.filter((row) => row.estado === 'pendiente'), [filter, routineRows]);
  const pendingCount = rows.filter((row) => row.estado === 'pendiente').length + routineRows.filter((row) => row.estado === 'pendiente').length;

  const review = async (action: 'aprobar' | 'rechazar') => {
    if (!selected || saving) return;
    let comment = '';
    if (action === 'rechazar') {
      comment = window.prompt('Indica el motivo del rechazo:')?.trim() || '';
      if (comment.length < 3) return;
    } else if (!window.confirm('Se creará la familia y se asignará una copia independiente a este usuario. ¿Aprobar?')) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    const { data, error: invokeError } = await supabase.functions.invoke('acoma-family-review', {
      body: { recomendacion_id: selected.id, accion: action, comentario: comment || null },
    });
    if (invokeError || data?.error) setError(data?.error || invokeError?.message || 'No pudimos resolver la propuesta.');
    else {
      setNotice(data?.message || (action === 'aprobar' ? 'Propuesta aprobada.' : 'Propuesta rechazada.'));
      setSelected(null);
      await load();
    }
    setSaving(false);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-950">Propuestas IA</h1>
            <p className="mt-1 text-gray-500">ACOMA trabaja en segundo plano. Revisa cada propuesta antes de asignarla.</p>
          </div>
          <div className="flex items-center gap-2">
            {pendingCount > 0 && <span className="rounded-full bg-amber-100 px-3 py-1.5 text-sm font-bold text-amber-800">{pendingCount} por revisar</span>}
            <button type="button" onClick={() => void load()} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <i className="ri-refresh-line mr-2" />Actualizar
            </button>
          </div>
        </div>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}
        {notice && <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-700">{notice}</div>}

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 p-4">
            <button onClick={() => setFilter('activas')} className={`rounded-lg px-4 py-2 text-sm font-bold ${filter === 'activas' ? 'bg-teal-800 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Pendientes</button>
            <button onClick={() => setFilter('todas')} className={`rounded-lg px-4 py-2 text-sm font-bold ${filter === 'todas' ? 'bg-teal-800 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Historial</button>
          </div>
          <div className="border-b border-gray-200 bg-violet-50/60 px-4 py-3">
            <h2 className="font-bold text-violet-950"><i className="ri-sparkling-2-line mr-2" />Propuestas de rutina</h2>
            <p className="mt-1 text-xs text-violet-700">Rutinas individuales generadas para cada socio.</p>
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-500"><i className="ri-loader-4-line mr-2 animate-spin" />Cargando propuestas...</div>
          ) : visibleRoutineRows.length === 0 ? (
            <div className="border-b border-gray-200 p-6 text-center text-sm text-gray-500">No hay propuestas de rutina en esta bandeja.</div>
          ) : (
            <div className="divide-y divide-gray-100 border-b border-gray-200">
              {visibleRoutineRows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => navigate(`/progreso?usuario=${encodeURIComponent(row.usuario_id)}&propuesta=${encodeURIComponent(row.id)}`)}
                  className="grid w-full gap-3 p-4 text-left hover:bg-gray-50 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-bold text-gray-900">{names[row.usuario_id] || 'Usuario'}</p>
                      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-bold text-violet-700">Rutina</span>
                    </div>
                    <p className="mt-1 font-semibold text-gray-700">{row.rutina_propuesta?.nombre || 'Rutina propuesta'}</p>
                    <p className="mt-1 line-clamp-2 text-sm text-gray-500">{row.resumen}</p>
                    <p className="mt-1 text-xs text-gray-400">Generada {new Date(row.created_at).toLocaleString('es-CL')}</p>
                  </div>
                  <span className={`w-fit rounded-full px-3 py-1 text-xs font-bold ${stateStyles[row.estado]}`}>{stateLabels[row.estado]}</span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700">Revisar <i className="ri-arrow-right-s-line text-xl" /></span>
                </button>
              ))}
            </div>
          )}
          <div className="border-b border-gray-200 bg-teal-50/60 px-4 py-3">
            <h2 className="font-bold text-teal-950"><i className="ri-node-tree mr-2" />Propuestas de familias</h2>
            <p className="mt-1 text-xs text-teal-700">Familias completas con etapas, sesiones y ejercicios.</p>
          </div>
          {loading ? (
            <div className="p-12 text-center text-gray-500"><i className="ri-loader-4-line mr-2 animate-spin" />Cargando propuestas...</div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center"><i className="ri-sparkling-2-line text-4xl text-gray-300" /><p className="mt-2 font-semibold text-gray-700">No hay propuestas en esta bandeja</p></div>
          ) : (
            <div className="divide-y divide-gray-100">
              {visible.map((row) => (
                <button key={row.id} type="button" onClick={() => setSelected(row)} className="grid w-full gap-3 p-4 text-left hover:bg-gray-50 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center">
                  <div className="min-w-0">
                    <p className="truncate font-bold text-gray-900">{row.usuario_id ? names[row.usuario_id] || 'Usuario' : 'Lote general'}</p>
                    <p className="mt-1 line-clamp-1 text-sm text-gray-500">{row.instrucciones}</p>
                    <p className="mt-1 text-xs text-gray-400">Solicitada por {row.solicitada_por_admin_id ? requesterNames[row.solicitada_por_admin_id] || 'equipo Mowe' : 'equipo Mowe'} · {new Date(row.created_at).toLocaleString('es-CL')}</p>
                  </div>
                  <span className={`w-fit rounded-full px-3 py-1 text-xs font-bold ${stateStyles[row.estado]}`}>{row.estado === 'generando' && <i className="ri-loader-4-line mr-1 animate-spin" />}{stateLabels[row.estado]}</span>
                  <i className="ri-arrow-right-s-line text-xl text-gray-400" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4">
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-5">
              <div>
                <div className="flex flex-wrap items-center gap-2"><h2 className="text-xl font-bold text-gray-950">{selected.propuesta?.title || 'Propuesta de familia'}</h2><span className={`rounded-full px-3 py-1 text-xs font-bold ${stateStyles[selected.estado]}`}>{stateLabels[selected.estado]}</span></div>
                <p className="mt-1 text-sm text-gray-500">{selected.usuario_id ? names[selected.usuario_id] || 'Usuario' : 'Lote general'}</p>
              </div>
              <button onClick={() => setSelected(null)} className="grid h-10 w-10 place-items-center rounded-lg hover:bg-gray-100" aria-label="Cerrar"><i className="ri-close-line text-2xl" /></button>
            </div>
            <div className="space-y-5 overflow-y-auto p-5">
              <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-bold uppercase tracking-wide text-slate-500">Solicitud del coach</p><p className="mt-2 text-sm text-slate-800">{selected.instrucciones}</p></div>
              {selected.error_acoma && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{selected.error_acoma.message || selected.error_acoma.code || 'ACOMA no pudo generar la propuesta.'}</div>}
              {selected.propuesta?.summary && <p className="text-sm text-gray-600">{selected.propuesta.summary}</p>}
              {(selected.propuesta?.families || []).map((family, familyIndex) => (
                <section key={`${family.name}-${familyIndex}`} className="overflow-hidden rounded-xl border border-gray-200">
                  <div className="bg-teal-50 p-4"><h3 className="font-bold text-teal-950">{family.name}</h3><p className="mt-1 text-sm text-teal-800">{family.description}</p><p className="mt-2 text-xs font-semibold text-teal-700">{family.level} · {family.frequency} sesiones por semana · {family.stages.length} etapas</p></div>
                  <div className="space-y-3 p-4">
                    {family.stages.map((stage, stageIndex) => (
                      <details key={`${stage.code}-${stageIndex}`} className="rounded-lg border border-gray-200" open={stageIndex === 0}>
                        <summary className="cursor-pointer p-3 font-semibold text-gray-800">Etapa {stageIndex + 1}: {stage.name} · {stage.durationDays} días</summary>
                        <div className="space-y-3 border-t border-gray-100 p-3">
                          {stage.sessions.map((session) => <div key={session.code}><p className="text-sm font-bold text-gray-800">{session.name}</p><div className="mt-2 grid gap-2 sm:grid-cols-2">{session.exercises.map((exercise) => <div key={`${session.code}-${exercise.exerciseId}`} className="rounded-lg bg-gray-50 p-2 text-xs text-gray-700"><span className="font-semibold">{exercise.exerciseName}</span><span className="block text-gray-500">{exercise.series} × {exercise.repetitions} · descanso {exercise.restSeconds}s</span></div>)}</div></div>)}
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              ))}
            </div>
            {selected.estado === 'pendiente' && <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:flex-row sm:justify-end"><button disabled={saving} onClick={() => void review('rechazar')} className="rounded-lg border border-red-300 px-5 py-2.5 font-bold text-red-700 hover:bg-red-50 disabled:opacity-50">Rechazar</button><button disabled={saving} onClick={() => void review('aprobar')} className="rounded-lg bg-teal-800 px-5 py-2.5 font-bold text-white hover:bg-teal-900 disabled:opacity-50">{saving ? 'Procesando...' : 'Aprobar y asignar'}</button></div>}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
